if (!window.CartGwpGuard) {
  window.CartGwpGuard = {
    locks: {},

    getLockKey(tier, variantId) {
      return tier || (variantId ? `variant:${variantId}` : 'gwp');
    },

    isLocked(key) {
      return !!this.locks[key];
    },

    setLocked(key, locked) {
      if (!key) return;
      if (locked) {
        this.locks[key] = true;
      } else {
        delete this.locks[key];
      }
    },

    itemProperties(item) {
      return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
    },

    getItemTier(item) {
      const properties = this.itemProperties(item);
      return properties._gwp_tier || properties.gwp_tier || '';
    },

    isSkuGwp(item) {
      const sku = String(item && item.sku ? item.sku : '');
      return sku.indexOf('GWP') !== -1;
    },

    isGwpItem(item) {
      const properties = this.itemProperties(item);
      return properties._gwp === 'true' || properties._gwp === true || !!this.getItemTier(item) || this.isSkuGwp(item);
    },

    async getCart() {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      return response.json();
    },

    async hasGift(tier, variantId) {
      const cart = await this.getCart();
      const targetTier = tier || '';
      const targetVariantId = String(variantId || '');

      return (cart.items || []).some((item) => {
        if (!this.isGwpItem(item)) return false;
        const itemTier = this.getItemTier(item);
        if (targetTier && itemTier) return itemTier === targetTier;
        if (targetTier && this.isSkuGwp(item)) return true;
        return targetVariantId && String(item.variant_id || item.id || '') === targetVariantId;
      });
    },

    async normalize() {
      const cart = await this.getCart();
      const seen = {};
      const changes = [];

      (cart.items || []).forEach((item) => {
        if (!this.isGwpItem(item)) return;

        const tier = this.getItemTier(item);
        const key = tier || (this.isSkuGwp(item) ? 'legacy-gwp' : `variant:${item.variant_id || item.id}`);

        if (seen[key]) {
          changes.push({ id: item.key, quantity: 0 });
          return;
        }

        seen[key] = true;

        if (Number(item.quantity || 0) > 1) {
          changes.push({ id: item.key, quantity: 1 });
        }
      });

      for (const change of changes) {
        await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify(change)
        });
      }

      return changes.length > 0;
    },

    async fetchCartDrawerSection() {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      return response.text();
    },

    redirectToCheckout() {
      window.__CHECKOUT_REDIRECTING__ = true;
      document.documentElement.classList.add('is-checkout-redirecting');
      window.location.href = '/checkout';
    }
  };
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.isOpenCartDrawer = this.dataset.isOpenCartDrawer;
        this.productPresaleGiftId = parseInt(this.getAttribute('data-product-presale-gift-id') ?? "0");


        // 1. Check for variant-radios[data-extended-details] (URL encoded JSON) near the form
        let extendedDetailsById = {};
        let radios = null;
        let searchNode = this.form;
        while (searchNode && !radios) {
          radios = searchNode.querySelector && searchNode.querySelector('variant-radios[data-extended-details]');
          searchNode = searchNode.parentElement;
        }
        if (!radios) {
          // If not found in ancestors, try document-level as a last resort
          radios = document.querySelector('variant-radios[data-extended-details]');
        }
        if (radios) {
          try {
            const encoded = radios.getAttribute('data-extended-details') || '';
            const decoded = decodeURIComponent(encoded);
            extendedDetailsById = JSON.parse(decoded);
          } catch (e) {
            console.warn('Could not parse extendedDetails from variant-radios', e);
          }
        } else {
          // 2. Fallback: Find the closest script[data-extended-details] outside the form
          let extendedDetails = null;
          let parent = this.form.parentElement;
          while (parent && !extendedDetails) {
            extendedDetails = parent.querySelector('script[type="application/json"][data-extended-details]');
            parent = parent.parentElement;
          }
          if (extendedDetails) {
            try {
              extendedDetailsById = JSON.parse(extendedDetails.textContent || '{}');
            } catch (e) {
              console.warn('Could not parse extendedDetails JSON', e);
            }
          }
        }
        this.extendedDetailsById = extendedDetailsById;

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // Returns a Promise resolving to the quantity of a given variant ID in the current cart (via cart.js API)
      getProductQuantityInCart(variantId) {
        return fetch('/cart.js', { credentials: 'same-origin' })
          .then(res => res.json())
          .then(cartData => {
            if (cartData && Array.isArray(cartData.items)) {
              const item = cartData.items.find(i => String(i.id) === String(variantId));
              return item ? item.quantity : 0;
            }
            return 0;
          })
          .catch(e => {
            console.error('Error fetching cart data from /cart.js:', e);
            return 0;
          });
      }

      getSectionsToRender() {
        return [
          {
            id: 'cart-upsell',
            section: 'cart-upsell',
            selector: '.cart-upsell',
          },
        ]
      }

      onUpdateSubmitBtn() {
        const ELM_warapper = this.closest('.presale-card-product-has-custom');
        if (ELM_warapper) {
          ELM_warapper.classList.add('product-added-to-cart');
          setTimeout(() => {
            ELM_warapper.classList.remove('product-added-to-cart');
          }, 1500)
        }
      }

      isGwpForm() {
        return this.dataset.isGwp === 'true' || !!this.closest('[data-thresholds-gift]');
      }

      getGwpTier() {
        return this.dataset.gwpTier || this.form.querySelector('[name="properties[_gwp_tier]"]')?.value || '';
      }

      shouldAutoDirectToCheckout() {
        return this.dataset.autoDirectToCheckout === 'true';
      }

      async refreshCartDrawerContents(isOpenDrawer) {
        if (!this.cart || !window.CartGwpGuard) return;
        const sectionHtml = await window.CartGwpGuard.fetchCartDrawerSection();
        this.cart.renderContents({ sections: { 'cart-drawer': sectionHtml } }, isOpenDrawer);
      }

      // Optimistic add for the drawer's quick-select upsell carousel: show a
      // stand-in line item the moment Add to cart is clicked, built from the
      // slide's own image/title/variant/price. Prepended because /cart/add puts
      // the newest item first, so the real row replaces it in the same slot
      // when the drawer re-renders. Returns null outside a quick-select slide —
      // free gifts deliberately keep the plain loading wheel.
      insertOptimisticUpsellRow() {
        const slide = this.closest('[data-cart-quick-select-slide]');
        const itemsList = document.querySelector('cart-drawer .drawer__cart-items-wrapper .cart-items');
        if (!slide || !itemsList) return null;

        const image = slide.querySelector('[data-cart-quick-select-image]');
        const title = slide.querySelector('.cart-quick-select__product-title')?.textContent.trim();
        const price = slide.querySelector('[data-cart-quick-select-price]')?.textContent.trim();
        const variantSelect = slide.querySelector('[data-cart-quick-select-variant]');
        const variantTitle = variantSelect?.options[variantSelect.selectedIndex]?.textContent.trim();

        const row = document.createElement('div');
        row.className = 'cart-item-wrapper cart-item-wrapper--optimistic';
        row.innerHTML = `
          <div class="cart-item">
            <div class="cart-item__media"></div>
            <div class="cart-item__details">
              <div class="cart-item__details-left">
                <div class="cart-item__header">
                  <span class="cart-item__name"></span>
                </div>
                <div class="cart-item__options">
                  <span class="cart-item__option_value"></span>
                </div>
                <div class="cart-item__price-wrapper">
                  <div class="cart-item__price"></div>
                </div>
              </div>
              <div class="cart-item__details-right"></div>
            </div>
          </div>`;
        row.querySelector('.cart-item__name').textContent = title || '';
        row.querySelector('.cart-item__option_value').textContent = variantTitle || '';
        row.querySelector('.cart-item__price').textContent = price || '';
        if (image) {
          const img = document.createElement('img');
          img.className = 'cart-item__image';
          img.src = image.currentSrc || image.src;
          img.alt = '';
          row.querySelector('.cart-item__media').appendChild(img);
        }

        itemsList.prepend(row);
        return row;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;
        const ELM_quickAdd = this.closest('.quick-add');
        if (ELM_quickAdd) {
          ELM_quickAdd.classList.remove('open');
          const ELM_quickAddBtnWrapper = ELM_quickAdd.querySelector('.quick-add__submit-wrapper');
          ELM_quickAddBtnWrapper && (ELM_quickAddBtnWrapper.style.top = 'auto');
        }

        this.handleErrorMessage();
        const formData = new FormData(this.form);
        const ELM_noCustomizeMsg = this.querySelector('.product-form__no-cutomize');

        if (formData.get('id') === '' && ELM_noCustomizeMsg) {
          this.classList.add('has-error')
          ELM_noCustomizeMsg?.classList.remove('hidden');
          return;
        } else {
          this.classList.remove('has-error')
          ELM_noCustomizeMsg?.classList.add('hidden');
        }

        const isGwpForm = this.isGwpForm();
        const gwpVariantId = formData.get('id');
        const gwpTier = this.getGwpTier();
        const gwpLockKey = isGwpForm && window.CartGwpGuard ? window.CartGwpGuard.getLockKey(gwpTier, gwpVariantId) : '';

        if (isGwpForm && window.CartGwpGuard) {
          if (window.CartGwpGuard.isLocked(gwpLockKey)) return;
          window.CartGwpGuard.setLocked(gwpLockKey, true);

          try {
            const cartChanged = await window.CartGwpGuard.normalize();
            const giftAlreadyExists = await window.CartGwpGuard.hasGift(gwpTier, gwpVariantId);

            if (cartChanged) {
              await this.refreshCartDrawerContents(this.isOpenCartDrawer);
            }

            if (giftAlreadyExists) {
              window.CartGwpGuard.setLocked(gwpLockKey, false);
              return;
            }
          } catch (e) {
            console.error('Error checking GWP cart state', e);
          }
        }

        // Optimistic add for drawer upsell products only — gift forms returned
        // above or fall through with isGwpForm true and are skipped here.
        let optimisticCartRow = null;
        if (!isGwpForm) {
          optimisticCartRow = this.insertOptimisticUpsellRow();
        }

        this.submitButton?.setAttribute('aria-disabled', true);
        this.submitButton?.classList.add('loading');
        this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const doAddToCartFetch = () => {
          fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              optimisticCartRow?.remove();
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton?.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton?.setAttribute('aria-disabled', true);
              this.submitButton?.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              //window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response });
            this.error = false;
            this.onUpdateSubmitBtn();

            // Tell the drawer which line to pulse once it re-renders (see
            // CartDrawer.applyPendingPulse). Set here so every renderContents
            // path below (normal add, GWP refresh, quick-add modal) picks it up.
            if (this.cart) this.cart._pulseVariantId = formData.get('id');

            let normalizedCart = false;
            if (isGwpForm && window.CartGwpGuard) {
              try {
                normalizedCart = await window.CartGwpGuard.normalize();
              } catch (e) {
                console.error('Error normalizing GWP cart state', e);
              }
            }

            if (this.shouldAutoDirectToCheckout()) {
              window.CartGwpGuard ? window.CartGwpGuard.redirectToCheckout() : window.location.href = '/checkout';
              return;
            }

            if (normalizedCart && this.cart) {
              await this.refreshCartDrawerContents(this.isOpenCartDrawer);
              return;
            }

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response, this.isOpenCartDrawer);
            }
          })
          .catch((e) => {
            console.error(e);
            optimisticCartRow?.remove();
          })
          .finally(() => {
            this.submitButton?.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
            if (isGwpForm && window.CartGwpGuard) {
              window.CartGwpGuard.setLocked(gwpLockKey, false);
            }
          });
        }

        const handleAddtoCart = () => {
          // Get the current variant's extended details and log it
          const variantId = formData.get('id');
          const variantDetails = this.extendedDetailsById[variantId];
          const add_to_cart_limit = variantDetails ? (variantDetails.addToCartLimit || variantDetails.add_to_cart_limit || 999) : 999;
          if (add_to_cart_limit > 0 && add_to_cart_limit < 999) {
            this.getProductQuantityInCart(variantId).then(currentCartQuantity => {
              if (currentCartQuantity >= add_to_cart_limit) {
                const limitReachedMessage = `You can only add up to ${add_to_cart_limit} of this item to your cart.`;
                console.warn(limitReachedMessage);
                // publish(PUB_SUB_EVENTS.cartError, {
                //   source: 'product-form',
                //   productVariantId: variantId,
                //   errors: limitReachedMessage,
                //   message: limitReachedMessage,
                // });
                // this.handleErrorMessage(limitReachedMessage);

                //stop loading
                this.submitButton?.classList.remove('loading');
                if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
                this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
                optimisticCartRow?.remove();

                //dispatch custom event
                const event = new CustomEvent('cartAddToCartLimitReached', {
                  detail: {
                    variantId: variantId,
                    limit: add_to_cart_limit,
                    qty_in_cart: currentCartQuantity,
                    addToCartLimitHeader: variantDetails ? (variantDetails.addToCartLimitHeader || '') : '',
                    addToCartLimitText: variantDetails ? (variantDetails.addToCartLimitText || '') : '',
                  },
                  bubbles: true,
                  composed: true
                });
                this.dispatchEvent(event);
                
                return;
              }

              const isOnBackorder = variantDetails ? (variantDetails.isOnBackorder == true) : false;
              if (isOnBackorder) {
                //stop loading
                this.submitButton?.classList.remove('loading');
                if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
                this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
                optimisticCartRow?.remove();

                //dispatch custom event
                const backorderEstimatedMessage = variantDetails ? (variantDetails.backorderEstimatedMessage || '') : '';
                const priceText = variantDetails ? (variantDetails.priceText || '') : '';
                const event = new CustomEvent('cartAddToCartBackorderRequested', {
                  detail: {
                    variantId: variantId,
                    backorderEstimatedMessage: backorderEstimatedMessage,
                    priceText: priceText,
                    addToCartHandler: doAddToCartFetch
                  },
                  bubbles: true,
                  composed: true
                });
                this.dispatchEvent(event);
                
                return;
              }
              // Proceed with add to cart if under limit
              doAddToCartFetch();
            });
            return;
          }

          const isOnBackorder = variantDetails ? (variantDetails.isOnBackorder == true) : false;
          if (isOnBackorder) {
            //stop loading
            this.submitButton?.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
            optimisticCartRow?.remove();

            //dispatch custom event
            const backorderEstimatedMessage = variantDetails ? (variantDetails.backorderEstimatedMessage || '') : '';
            const priceText = variantDetails ? (variantDetails.priceText || '') : '';
            const event = new CustomEvent('cartAddToCartBackorderRequested', {
              detail: {
                variantId: variantId,
                backorderEstimatedMessage: backorderEstimatedMessage,
                priceText: priceText,
                addToCartHandler: doAddToCartFetch
              },
              bubbles: true,
              composed: true
            });
            this.dispatchEvent(event);
            
            return;
          }
          
          doAddToCartFetch();
          return;
        }

        handleAddtoCart();

      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
